/**
 * useBadgeCounts — single source for every in-app badge count.
 *
 * Returns an object keyed by surface name with the number to render:
 *   {
 *     community:   N  — for BottomNav Community tab (pending event invites)
 *     tickets:     N  — for UserMenu Tickets & Invitations entry
 *     connections: N  — for UserMenu Share & Connections + Sharing chip
 *     dupes:       N  — household merge dupes (also under Share)
 *     total:       N  — sum, for the avatar dot in UserMenu
 *   }
 *
 * Each surface respects the user's notification prefs. If
 * notificationPrefs.eventInvitations.inApp === false, `community` and
 * `tickets` return 0 even when invites exist — user explicitly opted
 * out of seeing the dot. Same for friendInvites gating connections+dupes.
 *
 * Why one hook instead of three or four:
 *   Pre-2026-05-25 the badge logic was scattered:
 *     • BottomNav.pendingInvites           — App.jsx fetched + passed down
 *     • UserMenu.connectionNotifs           — Home.jsx fetched + passed down
 *     • UserMenu.dupeCount via useDupeCount — separate hook
 *   Each fetched independently, none honored the user's in-app prefs,
 *   and adding a new surface meant threading state through 3 components.
 *   With everything in one hook, BottomNav + UserMenu + future pages
 *   read the same object — and adding a new surface (e.g. unread shop
 *   tastings) is a single source change.
 *
 * Realtime via onSnapshot where the source is Firestore-native; window
 * 'focus' refresh for the legacy fetchEventInvitesForUser helper (uses
 * a one-shot getDocs internally). Cheap — getDocs runs only on focus,
 * not on every render.
 */

import { useEffect, useMemo, useState } from 'react'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '../firebase.js'
import { fetchEventInvitesForUser } from '../utils/eventInvites.js'
import { useDupeCount } from '../utils/dupeCount.js'
import { useNotificationPrefs } from './useNotificationPrefs.js'
// 2026-05-26 Phase B: inbox unread count rolls into the avatar dot
// so users see "you have new activity" without opening the page.
import { useInbox } from '../utils/notificationsInbox.js'

export function useBadgeCounts(user) {
  const uid = user?.uid

  // ── Per-pref gates ────────────────────────────────────────────────
  const { prefs } = useNotificationPrefs(uid)
  const eventInApp  = prefs?.eventInvitations?.inApp !== false
  const friendInApp = prefs?.friendInvites?.inApp !== false

  // ── Event invitations (counted once, fed by focus refresh) ────────
  const [eventInvitesCount, setEventInvitesCount] = useState(0)
  useEffect(() => {
    if (!uid) { setEventInvitesCount(0); return }
    let cancelled = false
    const refresh = async () => {
      try {
        const invites = await fetchEventInvitesForUser(db, user)
        if (!cancelled) {
          setEventInvitesCount(invites.filter(i => i.rsvp === 'pending').length)
        }
      } catch {/* non-fatal */}
    }
    refresh()
    const onFocus = () => refresh()
    window.addEventListener('focus', onFocus)
    return () => { cancelled = true; window.removeEventListener('focus', onFocus) }
    // We intentionally key on `uid` only. `user` is a Firebase auth
    // proxy whose identity changes on every displayName/photoURL tweak
    // — including it would re-fetch invites on every harmless profile
    // edit. Read `user` from closure inside `refresh()`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])

  // ── Connection invite responses (the "your friend accepted" badge)
  const [connectionResponseCount, setConnectionResponseCount] = useState(0)
  useEffect(() => {
    if (!uid) { setConnectionResponseCount(0); return }
    const q = query(collection(db, 'invites'), where('fromUserId', '==', uid))
    const unsub = onSnapshot(q, snap => {
      const unseen = snap.docs.filter(d => {
        const data = d.data()
        return data.status && data.status !== 'pending' && !data.hostSeen
      })
      setConnectionResponseCount(unseen.length)
    }, () => {})
    return () => unsub()
  }, [uid])

  // ── Household merge dupes ─────────────────────────────────────────
  const dupeCount = useDupeCount(user)

  // ── Notification inbox (unread) ───────────────────────────────────
  // Pulls notifications/{uid}/items entries with readAt == null.
  // Each entry maps to a route via item.routeKey; for simplicity v1
  // counts ALL unread as "inbox" (one bucket) regardless of route —
  // the routes already determine WHERE the underlying action fires.
  const { unreadCount: inboxUnread } = useInbox(uid, 50)

  // ── Aggregate (with pref gating) ──────────────────────────────────
  const counts = useMemo(() => {
    const community   = eventInApp  ? eventInvitesCount        : 0
    const tickets     = eventInApp  ? eventInvitesCount        : 0 // tickets surface = same source today
    const connections = friendInApp ? connectionResponseCount  : 0
    const dupes       = friendInApp ? dupeCount                : 0
    // inbox is not pref-gated — the user has already explicitly
    // opted in by getting a notification mirror written; if they
    // don't want it visible they can clear the row.
    const inbox       = inboxUnread
    return {
      community,
      tickets,
      connections,
      dupes,
      inbox,
      // Avatar dot — sum of everything the user might want to see.
      total: community + connections + dupes + inbox,
    }
  }, [eventInApp, friendInApp, eventInvitesCount, connectionResponseCount, dupeCount, inboxUnread])

  return counts
}
